import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster, useColorMode, Toaster } from '@mono/chakra';
import { HeaderRightPortal } from '@mono/layout';
import { isHttpError, useHttp, useHttpQuery } from '@mono/http-hook';
import { FC, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import Editor from './Editor';
import { EditorRef } from './types';
import { useCurrentOrganization } from '../organizations/hooks';
import { FileDetail } from '../file-browser/types';
import { PiGitDiffFill } from 'react-icons/pi';
import { useCurrentProject } from '../projects/hooks';
import { FaCode, FaEye, FaPencilRuler } from 'react-icons/fa';
import { getEditors } from './utils';

const FileEditorPage: FC = () => {
  const http = useHttp();
  const org = useCurrentOrganization();
  const project = useCurrentProject();
  const editorRef = useRef<EditorRef>(null);
  const colormode = useColorMode();
  const [params] = useSearchParams({ path: '' });
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const projectId = project.data?.project.id;
  const [showCodeEditor, setShowCodeEditor] = useState(false);

  const filePath = params.get('path') ?? '';

  const fileQuery = useHttpQuery<{ file: FileDetail }>({
    queryKey: ['file-editor', org?.id, projectId, filePath],
    config: {
      method: 'get',
      url:
        org && projectId
          ? `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/file`
          : '',
      params: { path: filePath },
      withCredentials: true,
    },
    options: {
      enabled: Boolean(org?.id && projectId && filePath),
    },
  });

  const editors = fileQuery.data?.file.mimeType
    ? getEditors(fileQuery.data?.file.mimeType)
    : [];

  const editor = useMemo(() => {
    if (showCodeEditor) {
      return 'default';
    }
    return params.get('editor') ?? (editors ? editors[0] : 'default');
  }, [showCodeEditor, editors, params]);

  const supportsDefaultEditor = editors.includes('default');

  const handleSave = async () => {
    if (!editorRef.current || !org?.id || !projectId || !filePath) {
      return;
    }
    if (await editorRef.current.hasError()) {
      toaster.create({
        type: 'error',
        description: 'Please fix the highlighted errors.',
      });
      return;
    }
    const { raw } = await editorRef.current.getValue();
    setIsSaving(true);
    try {
      await http.post(
        `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/files/versions`,
        { path: filePath, data: raw },
        { withCredentials: true }
      );
      await fileQuery.refetch();
      toaster.create({
        type: 'success',
        description: 'File saved as a new version.',
      });
    } catch (error) {
      const message = isHttpError(error)
        ? error.response?.data?.error || error.message
        : (error as Error)?.message ?? 'Unable to save file.';
      toaster.create({
        type: 'error',
        description: message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const navigateToRevisions = () => {
    navigate(`/${org?.slug}/p/${projectId}/revisions?path=${filePath}`);
  };

  const handlePreview = () => {
    window.open(
      `${new URL(
        `${window.location.origin}/api/v1/preview/${org?.slug}/${project.data?.project.slug}${filePath}`,
        window.location.origin
      ).toString()}`,
      '_blank'
    );
  };

  const toggleCodeEditor = () => {
    setShowCodeEditor((pre) => !pre);
  };

  if (!filePath) {
    return (
      <Card.Root maxW="lg" m="8">
        <Card.Body>
          <Text>Select a file from the project to edit.</Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Box position={'absolute'} top={0} left={0} width={'100%'} height={'100%'}>
      <Toaster />
      <HeaderRightPortal>
        <Box gap={2} display={'flex'}>
          <IconButton
            size={'sm'}
            as={'a'}
            variant={'surface'}
            rounded={100}
            onClick={handlePreview}
            colorPalette="teal"
          >
            <FaEye />
          </IconButton>
          {supportsDefaultEditor && editors.length > 1 && (
            <IconButton
              size={'sm'}
              as={'a'}
              variant={'surface'}
              rounded={100}
              onClick={toggleCodeEditor}
            >
              {!showCodeEditor ? <FaCode /> : <FaPencilRuler />}
            </IconButton>
          )}
          <IconButton
            size={'sm'}
            as={'a'}
            variant={'surface'}
            rounded={100}
            onClick={navigateToRevisions}
          >
            <PiGitDiffFill />
          </IconButton>
          <Button
            size="sm"
            variant="solid"
            onClick={handleSave}
            loading={isSaving}
            disabled={fileQuery.isLoading || fileQuery.isError}
            borderRadius={'3xl'}
          >
            Save
          </Button>
        </Box>
      </HeaderRightPortal>
      {fileQuery.isLoading ? (
        <Stack p="6" maxW="lg" gap="3">
          <Skeleton height="32px" />
          <Skeleton height="300px" />
        </Stack>
      ) : fileQuery.isError ? (
        <Alert.Root status="error" maxW="lg" m="6">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Unable to open file</Alert.Title>
            <Alert.Description>
              {(fileQuery.error as Error)?.message ?? 'Unknown error.'}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : fileQuery.data ? (
        <Editor
          ref={editorRef}
          colormode={colormode.colorMode}
          value={fileQuery.data.file.data}
          mime={fileQuery.data.file.mimeType ?? 'text'}
          editor={editor as any}
        />
      ) : null}
    </Box>
  );
};

export default FileEditorPage;
